import {
    FormControl,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import { useMemo } from "react";
import type React from "react";
import { useTranslation } from "shared/hooks/useTranslation";
import {
    buildGroupedCategoryOptions,
    groupLabel,
    type CategoryGroupId,
} from "constants/categoryGroups";
import { isExpenseCategory } from "constants/categories";

interface CategorySelectProps {
    value: string;
    onChange: (value: string | undefined) => void;
    disabled?: boolean;
    /** ML/rule suggestion shown first in the picker. */
    suggestedCategory?: string;
    /** Hint when value is Other or empty. */
    showReviewHint?: boolean;
}

export default function CategorySelect({
    value,
    onChange,
    disabled,
    suggestedCategory,
    showReviewHint = false,
}: CategorySelectProps) {
    const { t } = useTranslation();

    const options = useMemo(
        () => buildGroupedCategoryOptions(t, suggestedCategory),
        [t, suggestedCategory],
    );

    const needsReview = showReviewHint && (!value || value === "Other");

    let lastGroup: CategoryGroupId | null = null;

    return (
        <FormControl fullWidth size="small">
            <InputLabel>{t("forms.category")}</InputLabel>
            <Select
                value={value}
                label={t("forms.category")}
                onChange={(e) => onChange(e.target.value || undefined)}
                disabled={disabled}
            >
                <MenuItem value="">{t("analytics.uncategorized")}</MenuItem>
                {options.flatMap((opt) => {
                    const items: React.ReactNode[] = [];
                    if (lastGroup !== opt.groupId) {
                        lastGroup = opt.groupId;
                        items.push(
                            <ListSubheader key={`header-${opt.groupId}`} sx={{ fontWeight: 700, lineHeight: 2 }}>
                                {groupLabel(opt.groupId, t)}
                            </ListSubheader>,
                        );
                    }
                    items.push(
                        <MenuItem key={opt.category} value={opt.category}>
                            {opt.isSuggested ? `${opt.label} (${t("categorization.suggested")})` : opt.label}
                        </MenuItem>,
                    );
                    return items;
                })}
            </Select>
            {needsReview ? (
                <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
                    {t("categorization.helpClassify")}
                </Typography>
            ) : null}
            {suggestedCategory && isExpenseCategory(suggestedCategory) && !value ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    {t("categorization.suggestedCategoryHint", {
                        category: t(`categories.${suggestedCategory}`),
                    })}
                </Typography>
            ) : null}
        </FormControl>
    );
}
